'use client';

import React, { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { Loader2 } from 'lucide-react';

// The filter panel, downloaded when it is about to be needed rather than with
// the page: most visitors never open it. The buttons that open it call
// preloadFilterPanel() on hover, focus and touch, so the download starts a
// moment before the tap lands. (Preloading while the browser was idle was
// tried with the old sheet and counted as blocking time on every page.)

const load = () => import('./FilterPanel');
// A tap shows something at once: on a slow connection - or on a server that
// builds pages on demand - the panel's code can take seconds to arrive, and a
// button that does nothing for that long looks broken.
const FilterPanel = dynamic(load, {
  ssr: false,
  loading: () => (
    <div role="status" className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
      <div className="flex items-center gap-3 rounded-2xl bg-neutral-900 border border-neutral-800 px-5 py-4 text-sm font-semibold text-white shadow-2xl">
        <Loader2 aria-hidden="true" className="w-5 h-5 animate-spin text-gold" />
        {/* Client-only (ssr: false), so the page's language can be read here. */}
        <span>{document.documentElement.lang === 'fr' ? 'Filtres…' : 'Filters…'}</span>
      </div>
    </div>
  ),
});

let preloading: Promise<unknown> | null = null;
export function preloadFilterPanel() {
  preloading ??= load();
}

type Props = React.ComponentProps<typeof FilterPanel>;

export default function LazyFilterPanel(props: Props) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (props.isOpen) setMounted(true);
  }, [props.isOpen]);

  return mounted || props.isOpen ? <FilterPanel {...props} /> : null;
}
